import { ChevronRight, Contact } from 'lucide-react';
import Link from 'next/link';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { routeLink } from '../../../lib/route-link';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useContactBook } from '../../../lib/contact-book';
import { needsAttention } from '../../../lib/contact-rows';
import { Card, Typography } from '../ui';
import { AgentEntryRow } from './AgentEntryRow';
import styles from './FilesSummaryCard.module.css';

/**
 * The Contacts tile on Your Agents — the whole card opens `/contacts`.
 *
 * It sits beside Tasks / Routines / Files / Apps because contacts belong to the same question those
 * answer: what the agents have been doing and with whom. It replaced the drawer destination, which
 * put a hub-level surface in a nav list rather than on the hub.
 *
 * The stat line counts the two things this screen already reasons about — how many people there are
 * and how many are waiting on the viewer — using `needsAttention`, the SAME predicate the contacts
 * list splits its sections by, so the tile and the screen it opens can never disagree about who
 * counts. It falls back to the prose blurb until the read lands and for an empty book, rather than
 * announcing "0 contacts" to somebody who has not asked.
 *
 * Mirrors `FilesSummaryCard` and shares its stylesheet, so the five tiles are one row of one design.
 */
export function ContactsSummaryCard({
  pageName = 'agents',
  onSelect,
  entryRow = false,
  title,
}: {
  /** What the tile is called where it sits — "Contacts" on Your Agents (default), "Outreach" on the map. */
  title?: string;
  pageName?: string;
  /** Under the map home the tile is a TAB: this runs instead of navigating (the href stays for cmd-click). */
  onSelect?: () => void;
  /** Mobile Your Agents: the compact single-row entry tile instead of the glyph-over-stats summary. */
  entryRow?: boolean;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const state = useContactBook('');

  const open = (e?: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; button?: number }) => {
    // A modifier click keeps the link's own behaviour (a new tab); a plain click is the tab.
    if (onSelect && !(e?.metaKey || e?.ctrlKey || e?.shiftKey || (e?.button ?? 0) !== 0)) {
      e?.preventDefault();
      onSelect();
    }
    return logPageClickBi({ page_name: pageName, button_name: 'open_contacts' });
  };
  const route = '/contacts';

  const contacts = state.kind === 'ready' ? state.contacts : [];
  const waiting = contacts.filter(needsAttention).length;
  // "{n} need a reply" only when somebody actually asked; a bare count otherwise.
  const subtitle =
    contacts.length === 0
      ? t('contacts.description')
      : waiting
      ? t('contacts.stats', { count: contacts.length, waiting })
      : t('contacts.statsPlain', { count: contacts.length });

  if (entryRow) {
    return (
      <AgentEntryRow
        title={title ?? t('contacts.title')}
        subtitle={contacts.length === 0 ? undefined : subtitle}
        href={route}
        onClick={open}
        testId="agentsContacts"
      />
    );
  }

  return (
    <Card component={Link} interactive size="sm" className={styles.card} {...routeLink(basePath, route)} onClick={open}>
      <div className={styles.body}>
        <span className={styles.title}>
          <Contact size={16} aria-hidden />
          <Typography variant="h4" color="strong" component="span" className={styles.titleText}>
            {title ?? t('contacts.title')}
          </Typography>
        </span>
        {/* One line, ellipsised — the tile is a fixed 72 and the prose fallback is a sentence, which
            wrapped to two lines and grew the whole row when Files hit the same case. */}
        <Typography variant="p3" color="secondary" noWrap>
          {subtitle}
        </Typography>
      </div>
      <ChevronRight size={16} className={`${styles.chevron} dir-flip`} aria-hidden />
    </Card>
  );
}
